<template>
  <v-container>
    <v-card>
      <v-card-title>
        {{ canManage ? $t('SUPPORT_ALL_TICKETS') : $t('SUPPORT_MY_TICKETS') }}
        <v-spacer></v-spacer>
        <v-btn color="primary" :to="{ name: routeName.SUPPORT }">
          <v-icon left>add</v-icon>
          {{ $t('SUPPORT_NEW') }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              clearable
              :label="$t('SUPPORT_STATUS')"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.area"
              :items="areaOptions"
              item-text="label"
              item-value="value"
              clearable
              :label="$t('SUPPORT_AREA')"
              @change="onFilterChange"
            ></v-select>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="items"
          :server-items-length="totalItems"
          :loading="loading"
          hide-default-footer
          class="elevation-1"
          @click:row="openTicket"
        >
          <template v-slot:[`item.severity_label`]="{ item }">
            <v-chip small :color="severityColor(item.severity)" text-color="white">
              {{ item.severity_label }}
            </v-chip>
          </template>
          <template v-slot:[`item.status_label`]="{ item }">
            <v-chip small :color="statusColor(item.status)" text-color="white">
              {{ item.status_label }}
            </v-chip>
          </template>
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn icon color="primary" :title="$t('SHOW')" @click.stop="openTicket(item)">
              <v-icon>visibility</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <Paginator
          :total-items="totalItems"
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          @change="fetchData"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { supportService } from '../../utils/services/support.service';
import Paginator from '../../components/shared/Paginator';

export default {
  name: 'SupportList',
  components: { Paginator },
  data() {
    return {
      loading: false,
      items: [],
      totalItems: 0,
      pagination: { page: 1, itemsPerPage: 20 },
      filters: { status: null, area: null },
      statusOptions: [],
      areaOptions: [],
    };
  },
  computed: {
    canManage() {
      return this.can('manage support');
    },
    headers() {
      const headers = [
        { text: '#', value: 'id' },
        { text: this.$t('SUPPORT_AREA'), value: 'area_label' },
        { text: this.$t('SUPPORT_TITLE'), value: 'title' },
        { text: this.$t('SUPPORT_SEVERITY'), value: 'severity_label' },
        { text: this.$t('SUPPORT_STATUS'), value: 'status_label' },
        { text: this.$t('SUPPORT_CREATED_AT'), value: 'created_at' },
        { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'right' },
      ];
      if (this.canManage) {
        headers.splice(1, 0, { text: this.$t('SUPPORT_SENT_BY'), value: 'user.name' });
      }
      return headers;
    },
  },
  created() {
    this.fetchOptions();
    this.fetchData();
  },
  methods: {
    async fetchOptions() {
      try {
        const response = await supportService.getOptions();
        this.statusOptions = response.data.statuses;
        this.areaOptions = response.data.areas;
      } catch (error) {
        this.handleError(error, this.errors);
      }
    },

    async fetchData() {
      this.loading = true;
      try {
        const response = await supportService.index({
          page: this.pagination.page,
          itemsPerPage: this.pagination.itemsPerPage,
          filters: {
            status: this.filters.status,
            area: this.filters.area,
          },
        });
        this.items = response.data.data;
        this.totalItems = response.data.meta ? response.data.meta.total : 0;
      } catch (error) {
        this.handleError(error, this.errors);
      } finally {
        this.loading = false;
      }
    },

    onFilterChange() {
      this.pagination.page = 1;
      this.fetchData();
    },

    openTicket(item) {
      this.$router.push({ name: this.routeName.SUPPORT_SHOW, params: { id: item.id } });
    },

    severityColor(severity) {
      if (severity === 'BLOCKING') return 'red darken-2';
      if (severity === 'HIGH') return 'orange darken-2';
      if (severity === 'MEDIUM') return 'blue darken-1';
      return 'grey';
    },

    statusColor(status) {
      if (status === 'OPEN') return 'red darken-1';
      if (status === 'IN_PROGRESS') return 'orange darken-2';
      if (status === 'RESOLVED') return 'green darken-1';
      return 'grey darken-1';
    },

    formatDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleString('it-IT');
    },
  },
};
</script>
